import { useEffect, useState, type ChangeEvent } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Checkbox } from '../atoms/Checkbox/Checkbox';
import { FieldError } from '../atoms/FieldError/FieldError';
import { Input } from '../atoms/Input/Input';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import {
  checkDoseGuard,
  type DoseGuardResult,
  type DoseGuardSettings,
} from '../../services/dosingScheduleService';

export type GuardElementValue = '' | DoseGuardSettings['element'];
export type GuardProductValue = '' | DoseGuardSettings['product'];

/** Form state of the dose guard (strings as typed). */
export interface DoseGuardFormValues {
  readonly element: GuardElementValue;
  readonly product: GuardProductValue;
  /** custom: "1 ml raises {liters} l by {raise} {unit}". */
  readonly raise: string;
  readonly liters: string;
}

export const EMPTY_DOSE_GUARD: DoseGuardFormValues = { element: '', product: '', raise: '', liters: '100' };

function num(raw: string): number | null {
  const n = Number(raw.trim().replace(',', '.'));
  return raw.trim() !== '' && Number.isFinite(n) && n > 0 ? n : null;
}

export function toDoseGuardFormValues(settings: DoseGuardSettings | null | undefined): DoseGuardFormValues {
  if (!settings) return EMPTY_DOSE_GUARD;
  return {
    element: settings.element,
    product: settings.product,
    raise: settings.product === 'custom' && settings.strength ? String(settings.strength / 100) : '',
    liters: '100',
  };
}

/** The settings to save, or null when the plan is not guarded / incomplete. */
export function toDoseGuardSettings(values: DoseGuardFormValues): DoseGuardSettings | null {
  if (values.element === '' || values.product === '') return null;
  if (values.product !== 'custom') return { element: values.element, product: values.product };
  const raise = num(values.raise);
  const liters = num(values.liters);
  return { element: values.element, product: 'custom', strength: raise !== null && liters !== null ? raise * liters : null };
}

/** Warn / block results need an explicit confirmation before saving. */
export function needsConfirmation(result: DoseGuardResult | null): boolean {
  return result !== null && (result.status === 'warn' || result.status === 'block');
}

const UNIT: Record<DoseGuardSettings['element'], string> = { alkalinity: 'dKH', calcium: 'ppm', magnesium: 'ppm' };

export interface DoseGuardFieldsProps {
  aquariumId: string;
  values: DoseGuardFormValues;
  onChange: (values: DoseGuardFormValues) => void;
  doseAmount: number | null;
  doseUnit: string;
  timesPerDay: number | null;
  confirmed: boolean;
  onConfirmedChange: (confirmed: boolean) => void;
  onResult: (result: DoseGuardResult | null) => void;
  confirmError?: boolean;
}

/**
 * Reef Guard dose guard (#1822) inside the dosing plan form: what the plan
 * doses, then — checked live against the aquarium's volume and the shared caps
 * — what it adds per dosing day. Above a cap the user confirms explicitly.
 */
export function DoseGuardFields({
  aquariumId, values, onChange, doseAmount, doseUnit, timesPerDay, confirmed, onConfirmedChange, onResult, confirmError = false,
}: DoseGuardFieldsProps) {
  const { t, i18n } = useTranslation();
  const [result, setResult] = useState<DoseGuardResult | null>(null);
  const settings = toDoseGuardSettings(values);
  const key = JSON.stringify([settings, doseAmount, doseUnit, timesPerDay]);

  useEffect(() => {
    let cancelled = false;
    if (!settings || doseAmount === null || doseAmount <= 0) {
      setResult(null);
      onResult(null);
      return undefined;
    }
    const timer = window.setTimeout(() => {
      checkDoseGuard(aquariumId, {
        dose_guard: settings,
        dose_amount: doseAmount,
        dose_unit: doseUnit,
        frequency_type: timesPerDay !== null ? 'multiple_times_daily' : 'daily',
        frequency_value: timesPerDay,
      })
        .then((r) => {
          if (cancelled) return;
          setResult(r);
          onResult(r);
        })
        .catch(() => {
          if (cancelled) return;
          setResult(null);
          onResult(null);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aquariumId, key]);

  const set = (patch: Partial<DoseGuardFormValues>) => onChange({ ...values, ...patch });
  const unit = values.element !== '' ? UNIT[values.element] : '';
  const fmt = (n: number) => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(n);
  const productOptions = values.element === 'alkalinity'
    ? (['balling_light_kh', 'sangokai_kh', 'custom', 'kalkwasser'] as const)
    : (['custom'] as const);

  return (
    <Stack gap="3" data-testid="dose-guard">
      <Text color="muted">{t('dosing.guard.intro')}</Text>
      <Select
        id="dose-guard-element"
        label={t('dosing.guard.element')}
        value={values.element}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => {
          const element = e.target.value as GuardElementValue;
          set({ element, product: element === '' ? '' : element === 'alkalinity' ? values.product : 'custom' });
        }}
        options={[
          { value: '', label: t('dosing.guard.elementNone') },
          { value: 'alkalinity', label: t('dosing.guard.elements.alkalinity') },
          { value: 'calcium', label: t('dosing.guard.elements.calcium') },
          { value: 'magnesium', label: t('dosing.guard.elements.magnesium') },
        ]}
      />
      {values.element !== '' && (
        <Select
          id="dose-guard-product"
          label={t('dosing.guard.product')}
          value={values.product}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => set({ product: e.target.value as GuardProductValue })}
          placeholder={t('dosing.guard.productPlaceholder')}
          options={productOptions.map((p) => ({ value: p, label: t(`dosing.guard.products.${p}`) }))}
        />
      )}
      {values.product === 'custom' && (
        <Stack gap="2">
          <Text>{t('dosing.guard.customLabel', { unit })}</Text>
          <Input
            id="dose-guard-liters"
            inputMode="decimal"
            label={t('dosing.guard.customLiters')}
            value={values.liters}
            onChange={(e: ChangeEvent<HTMLInputElement>) => set({ liters: e.target.value })}
          />
          <Input
            id="dose-guard-raise"
            inputMode="decimal"
            label={t('dosing.guard.customRaise', { unit })}
            value={values.raise}
            onChange={(e: ChangeEvent<HTMLInputElement>) => set({ raise: e.target.value })}
          />
        </Stack>
      )}
      {values.product === 'kalkwasser' && (
        <Alert tone="info" live="off" compact>{t('dosing.guard.kalkwasser')}</Alert>
      )}
      {result !== null && result.status !== 'not_checked' && (
        <Alert
          tone={result.status === 'block' ? 'danger' : result.status === 'warn' ? 'warning' : result.status === 'ok' ? 'success' : 'info'}
          live="polite"
          compact
          data-testid="dose-guard-result"
        >
          <Stack gap="1">
            {result.perDay !== null ? (
              <Text>{t('dosing.guard.perDay', { perDay: fmt(result.perDay), unit: result.unit, ml: fmt(result.mlPerDay ?? 0), volume: fmt(result.volumeLiters ?? 0) })}</Text>
            ) : (
              <Text>{t(`dosing.guard.status.${result.status}`)}</Text>
            )}
            {result.status === 'block' && result.cap !== null && (
              <Text>{t('dosing.guard.block', { cap: fmt(result.cap), unit: result.unit, days: result.splitDays ?? 2 })}</Text>
            )}
            {result.status === 'warn' && result.reason === 'over_soft' && result.cap !== null && (
              <Text>{t('dosing.guard.warn', { cap: fmt(result.cap), unit: result.unit })}</Text>
            )}
            {result.projectionDays !== null && (
              <Text>{t('dosing.guard.projection', { days: fmt(result.projectionDays) })}</Text>
            )}
          </Stack>
        </Alert>
      )}
      {needsConfirmation(result) && (
        <Stack gap="1">
          <Checkbox
            id="dose-guard-confirm"
            label={t('dosing.guard.confirm')}
            checked={confirmed}
            onChange={(e) => onConfirmedChange(e.target.checked)}
            aria-invalid={confirmError || undefined}
            aria-describedby={confirmError ? 'dose-guard-confirm-error' : undefined}
          />
          {confirmError && <FieldError id="dose-guard-confirm-error">{t('dosing.guard.confirmRequired')}</FieldError>}
        </Stack>
      )}
    </Stack>
  );
}
